import React, { useState, useEffect, useRef, createRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Container, Typography } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Table from '../../components/Table/Table';
import { LiveDatasheetDefinition } from '../../pageDefinition/LiveDatasheetDefinition';
import { setDesignDefinition } from '../../redux/designDefinitionSlice';
import PrimaryLinks from '../../components/PrimaryLinks/PrimaryLinks';
import SecondaryLinks from '../../components/SecondaryLinks/SecondaryLinks';
import Package from '../../components/Package/Package';
import { HeadingContainer, SecondContainer, TitleContainer } from './styles';
import Description from '../../components/Description/Description';
import Simulation from '../../components/Simulation/Simulation';
import NavigationLinks from '../../components/NavigationLinks/NavigationLinks';
import FloatingNavigation from '../../components/FloatingNavigation/FloatingNavigation';
import Feedback from '../../components/Feedback/Feedback';
import { FONT_SIZES } from '../../styles/constants';
import { recordEvent } from '../../utils/analytics/local';
import { sendAdobeEvent } from '../../utils/analytics/adobe';
import { APP_NAME, BASENAME } from '../../utils/constants/general';

const LiveDatasheet = () => {
	const { t } = useTranslation();
	const { partName } = useParams();
	const [partDefinition, setPartDefinition] = useState(null);
	const [currentSimulation, setCurrentSimulation] = useState(null);
	const dispatch = useDispatch();
	const [numberOfSimulations, setNumberOfSimulations] = useState('');
	const navigate = useNavigate();
	const isLargeScreen = useMediaQuery('(min-width:1400px)');
	const calledOnce = useRef(false);
	const renderAfterCalled = useRef(false);

	if (calledOnce.current) {
		renderAfterCalled.current = true;
	}

	useEffect(() => {
		const DatasheetDefinition = new LiveDatasheetDefinition(partName);
		const definition = DatasheetDefinition.pageDefinition.gainDefinition();

		async function fetchData() {
			try {
				if (!definition) {
					navigate('/', { state: { statusCode: 404 } });
					return;
				}
				setPartDefinition(definition);
				setNumberOfSimulations(definition.simulations.length);
				dispatch(setDesignDefinition(definition));
			} catch (err) {
				console.error(err);
				navigate('/', { state: { statusCode: 404 } });
			}
		}

		if (partName) {
			if (!calledOnce.current) {
				calledOnce.current = true;
				let referrer = document.referrer || null;
				if (referrer && referrer.includes(BASENAME)) {
					referrer = null;
				}
				recordEvent('spiceLiveModelView', JSON.stringify({ mappingName: definition.defaultMappingName }), referrer);
				sendAdobeEvent(
					'PageView',
					window.location.href,
					APP_NAME + ' | ' + definition.defaultMappingName,
					null,
					null,
					definition.wpn
				);
			}
			fetchData();
		}
	}, [partName, dispatch, navigate]);

	useEffect(() => {
		if (numberOfSimulations > 0) {
			const observer = new IntersectionObserver(
				entries => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							const simulationNumber = parseInt(entry.target.getAttribute('data-simulation-number'));
							setCurrentSimulation(simulationNumber);
						}
					});
				},
				{ threshold: 0.2 }
			);

			simulationRefs.current.forEach((ref, index) => {
				const section = ref.current;
				if (section) {
					section.setAttribute('data-simulation-number', index);
					observer.observe(section);
				}
			});

			return () => {
				if (simulationRefs.current) {
					simulationRefs.current.forEach(ref => {
						const section = ref.current;
						observer.unobserve(section);
					});
				}
			};
		}
	}, [numberOfSimulations]);

	const bottomRef = useRef();

	useEffect(() => {
		if (numberOfSimulations > 0) {
			const observer = new IntersectionObserver(
				entries => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							document.querySelectorAll('.floating-button').forEach(button => {
								button.style.display = 'none';
							});
						} else {
							document.querySelectorAll('.floating-button').forEach(button => {
								button.style.display = 'block';
							});
						}
					});
				},
				{ threshold: 0.5 }
			);

			observer.observe(bottomRef.current);

			return () => observer.disconnect();
		}
	}, [numberOfSimulations]);

	const simulationRefs = useRef([]);
	simulationRefs.current = Array(numberOfSimulations)
		.fill(null)
		.map((_, i) => simulationRefs.current[i] ?? createRef());

	return (
		partDefinition && (
			<Container maxWidth="lg">
				<TitleContainer>
					<Typography
						variant="h1"
						sx={{
							fontSize: { xs: FONT_SIZES.ONSEMI_TITLE, lg: FONT_SIZES.H1 },
							fontWeight: 800
						}}>
						{t('components.header.title') + ' '}
						<span style={{ fontSize: '0.7em', verticalAlign: 'sub' }}>Beta</span>
						{' | ' + (partDefinition && partDefinition.mainHeading)}
					</Typography>
					<Feedback />
				</TitleContainer>
				{partDefinition.primaryLinks &&
				partDefinition.longDescription &&
				partDefinition.secondaryLinks &&
				partDefinition.package ? (
					<HeadingContainer>
						<div>
							{partDefinition.primaryLinks ? <PrimaryLinks /> : null}
							{partDefinition.longDescription ? <Description /> : null}
						</div>
						<SecondContainer>{partDefinition.secondaryLinks ? <SecondaryLinks /> : null}</SecondContainer>
						{partDefinition.package ? <Package /> : null}
					</HeadingContainer>
				) : null}
				{partDefinition.table ? <Table /> : null}

				{numberOfSimulations > 0 &&
					Array.from({ length: numberOfSimulations }, (_, index) => (
						<Container key={`container-${index}`}>
							{!isLargeScreen && (
								<NavigationLinks
									numberOfSimulations={numberOfSimulations}
									partDefinition={partDefinition}
									simulationRefs={simulationRefs}
								/>
							)}
							<Simulation ref={simulationRefs.current[index]} simulationNumber={index} />
						</Container>
					))}

				{isLargeScreen && (
					<FloatingNavigation
						numberOfSimulations={numberOfSimulations}
						currentSimulation={currentSimulation}
						partDefinition={partDefinition}
						simulationRefs={simulationRefs}
					/>
				)}

				<div ref={bottomRef} style={{ height: '1px' }} />
			</Container>
		)
	);
};

export default LiveDatasheet;
